// Vendor Program v2 PR 3 — void modal for a VFI invoice row on the
// Accounting page (reason required; reuses the existing void endpoint /
// useVoidVendorInvoice mutation).

import { useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useVoidVendorInvoice } from "@/hooks/portfolioAdmin/useVendorBilling";
import type { VendorInvoiceDto } from "@/api/portfolioAdmin/types";

interface VoidDraftModalProps {
  invoice: VendorInvoiceDto;
  open: boolean;
  onClose: () => void;
}

export function VoidDraftModal({ invoice, open, onClose }: VoidDraftModalProps) {
  const toast = useToast();
  const voidMutation = useVoidVendorInvoice(invoice.id);
  const [reason, setReason] = useState("");

  const onConfirm = async () => {
    try {
      await voidMutation.mutateAsync({ reason: reason.trim() });
      toast.success(`Invoice ${invoice.invoiceNumber} voided.`);
      setReason("");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={`Void invoice ${invoice.invoiceNumber}?`}
      description="A void is permanent and requires a reason. The invoice number is never reused."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={voidMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void onConfirm()}
            disabled={!reason.trim() || voidMutation.isPending}
            data-testid="void-draft-confirm"
          >
            {voidMutation.isPending ? "Voiding…" : "Void invoice"}
          </Button>
        </>
      }
    >
      <label className="flex flex-col gap-space-1 text-caption">
        <span className="text-gray-mid uppercase tracking-label">Reason (required)</span>
        <textarea
          className="min-h-[3.5rem] px-space-2 py-space-1 border border-border-light rounded-md text-small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="void-draft-reason"
        />
      </label>
    </Modal>
  );
}
